import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Shelf from '../../components/widgets/Shelf';
import Spinner from '../../components/widgets/Spinner';
import Button from '../../components/atoms/Button';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import ChatConversation from '../../chatView/components/ChatConversation';
import VerdictBadge from './VerdictBadge';
import PromptTitleEditor from './PromptTitleEditor';
import PromptPreview from './PromptPreview';
import { MONITOR_TITLE_MAX_LENGTH, TRANSLATION_KEY } from '../constants';
import { monitorShape, verdictShape } from '../propShapes';
import { formatNextRunLabel } from '../utils';
import css from './ThreadShelf.css';

const SHELF_KEY = `${TRANSLATION_KEY}.SHELF`;
const CARD_KEY = `${TRANSLATION_KEY}.CARD`;

const ThreadShelf = ({ open, monitor = null, verdict = null, chat, onClose, onSaveTitle, onOpenMonitorDialog }) => {
    const [isRenaming, setIsRenaming] = useState(false);

    useEffect(() => {
        setIsRenaming(false);
    }, [monitor?.monitor_id]);

    const sessionReady =
        monitor != null && chat.loadingSessionId == null && chat.currentSessionId === monitor.session_id;
    const firstRunLabel = monitor && formatNextRunLabel(monitor.next_run_at);

    return (
        <Shelf
            open={open}
            onClose={onClose}
            headerText={`${SHELF_KEY}.TITLE`}
            shelfSize="xlarge"
            enterAnimationDisabled={false}
            contentContainerClass={css.shelfContent}
        >
            {monitor && (
                <div className={css.container}>
                    <div className={css.header}>
                        <div className={css.badgeRow}>
                            <VerdictBadge verdict={verdict} />
                        </div>
                        {isRenaming ? (
                            <div className={css.titleEditor}>
                                <PromptTitleEditor
                                    initialTitle={monitor.title ?? ''}
                                    maxLength={MONITOR_TITLE_MAX_LENGTH}
                                    allowEmpty
                                    onSave={onSaveTitle}
                                    onDone={() => setIsRenaming(false)}
                                />
                            </div>
                        ) : (
                            <div className={css.titleRow}>
                                <div className={classNames(css.title, { [css.untitled]: !monitor.title })}>
                                    {monitor.title || <Translate id={`${CARD_KEY}.UNTITLED`} />}
                                </div>
                                <Icon
                                    name="pencil"
                                    size={IconSizes.LARGE}
                                    className={css.editIcon}
                                    hoverText={`${CARD_KEY}.RENAME`}
                                    onClick={() => setIsRenaming(true)}
                                />
                            </div>
                        )}
                        <PromptPreview
                            key={monitor.monitor_id}
                            prompt={monitor.user_query}
                            className={css.promptBlock}
                            compact
                        />
                        <Button
                            type="link"
                            className={css.editPromptLink}
                            Icon="pencil"
                            iconSize={IconSizes.X_SMALL}
                            iconVariant={IconVariants.ACTIVE}
                            iconClass={css.editPromptIcon}
                            onClick={onOpenMonitorDialog}
                        >
                            <Translate id={`${CARD_KEY}.EDIT_MONITOR`} />
                        </Button>
                    </div>
                    <div className={css.conversation}>
                        {!sessionReady && <Spinner show />}
                        {sessionReady && !chat.messages.length && (
                            <div className={css.emptyThread}>
                                {firstRunLabel ? (
                                    <Translate id={`${CARD_KEY}.FIRST_RUN`} data={{ firstRunLabel }} />
                                ) : (
                                    <Translate id={`${CARD_KEY}.NO_RUNS_YET`} />
                                )}
                            </div>
                        )}
                        {sessionReady && chat.messages.length > 0 && <ChatConversation chat={chat} />}
                    </div>
                </div>
            )}
        </Shelf>
    );
};

ThreadShelf.propTypes = {
    open: PropTypes.bool.isRequired,
    monitor: monitorShape,
    verdict: verdictShape,
    chat: PropTypes.shape({
        messages: PropTypes.arrayOf(PropTypes.object).isRequired,
        currentSessionId: PropTypes.string,
        loadingSessionId: PropTypes.string,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onSaveTitle: PropTypes.func.isRequired,
    onOpenMonitorDialog: PropTypes.func.isRequired,
};

export default ThreadShelf;
